/**
 * レポートデータ更新画面。エントリ（Main）・ルート初期化・UI（Content）は同一ファイル（component.mdc §1）。 グローバル Redux は API の法人番号等の参照用。タスク一覧はローカル
 * reportDataUpdateStore が正。
 */
import * as React from 'react'
import { CircularProgress, Link } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom'
import { shallowEqual, useSelector, useStore } from 'react-redux'

import LoadingMessage from '../../../_components/LoadingMessage'
import Forbidden from '../../../_components/Forbidden'
import CustomTooltip from '../../../_components/CustomTooltip'
import { useToast } from '../../../_components/Toast'
import { SpinnerButton } from '../../common/CustomButton'
import { formatBytes } from '../DataMapper/extra/helpers'
import { errorLog } from '../../../common/logger/Logger'
import { formatCommaDigit } from '../../../common/utils/NumberUtils'
import { downloadGuide } from '../../../common/utils/FileUtils'
import { PathInfoList } from '../../../_constants/PathInfoList'
import {
  accountingUploadTaskStatus,
  dataCreateStatus,
  dataFixStatus,
  operationKind,
  titleName,
  viewId
} from '../../../_constants/Code'
import { useAccountingUploadErrorModal } from './components/AccountingUploadErrorModal'
import { useDataImportGuideModal } from './components/DataImportGuideModal'
import { useFixCancelModal } from './components/FixCancelModal'
import { useDataFixConfirmModal } from './components/DataFixConfirmModal'
import { useDataUpdateConfirmModal } from './components/DataUpdateConfirmModal'
import {
  calcOverallProgress,
  formatAccountingTarget,
  formatGenericKindRowMeta,
  formatGenericKindStatusMeta,
  extractQuarterLabel,
  formatPeriodLabelForHeader,
  formatPeriodTypeLabel,
  formatTaskScreenDateTime,
  formatOperationDuration,
  formatUploadFileListDateTime,
  formatUploadFileListNumericCell,
  formatUploadFileListUserCell,
  groupGenericDataKindsBySuperKind,
  uploadFileListStatusBadgeProps,
  filterAccountingUploadTasksForParentContext,
  isFileLessUpdateAllowed,
  isFinancialRowAccountingError,
  isFinancialRowUploadable,
  shouldShowProgressSummary,
  statusBadgeProps
} from './extra/helpers'
import { exportMappingToExcel } from './extra/exportMappingToExcel'
import { buildMappingExportRows } from './extra/mappingExportModel'
import { refreshReportTaskScreen } from './extra/refreshTaskScreen'
import {
  fetchAllChrofyColumns,
  fetchAllMappingSettings,
  fetchConversionTableList,
  fetchGenericTaskScreen,
  fetchJudgmentRules,
  postCopyPreviousMappingBulk,
  postCreateData,
  postFixCancelData,
  postFixData,
  postStartGenericDataValidationBulk
} from './extra/services'
import {
  ACTION_TYPES,
  ReportDataUpdateProviderStore,
  SCREEN_STATUS,
  useReportDataUpdateDispatch,
  useReportDataUpdateSelector,
  useReportDataUpdateStore
} from './extra/store'
import { GENERIC_DATA_KIND_GENERIC_ITEM, PERIOD_TYPE_QUARTER } from './extra/types'
import {
  MSG_DATA_UPDATE_FILES_NOT_ALL_UPLOADED,
  MSG_DATA_UPDATE_OTHER_PERIOD_CREATING,
  MSG_VALIDATION_BULK_NO_PENDING,
  MSG_MAPPING_EXPORT_DISABLED,
  MSG_VALIDATION_DISABLED_TASK_CREATING,
  allUploadFilesAreUploaded,
  hasPendingValidationKinds,
  validateDataUpdatePreconditions,
  validateDataValidationBulkPreconditions
} from './extra/validation'

/** 画面上部に表示する API・操作エラー（インラインキー） */
const INLINE_ERROR_KEY_PAGE_ACTION = 'pageAction'

/** 「データ更新」ボタン直下に表示するインラインキー */
const INLINE_ERROR_KEY_DATA_UPDATE = 'dataUpdate'

/** 「データ確定」ボタン直下に表示するインラインキー */
const INLINE_ERROR_KEY_DATA_FIX = 'dataFix'

/** データ更新（タスク一覧）へ戻る（FileManagement / GenericMapping と同系の上階層リンク） */
function BackToTaskListLink() {
  return (
    <div className="centered-container">
      <div className="bg main">
        <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
          <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/data/taskList">
            <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
              <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
              <span className="text-h5 font-bold"> {titleName.taskList}に戻る </span>
            </span>
          </Link>
        </div>
      </div>
    </div>
  )
}

/**
 * ルートパラメータ・クエリをローカルストアへ反映する（SR-001, SR-002）。 Provider 内でのみ使用する。
 *
 * @param {{ dataCreateTaskKeyParam?: string; periodTypeFromUrl: string | null; children: React.ReactNode }} props
 * @returns {React.ReactElement}
 */
function ReportDataRouteInit({ dataCreateTaskKeyParam, periodTypeFromUrl, children }) {
  const dispatch = useReportDataUpdateDispatch()

  React.useLayoutEffect(() => {
    const raw = dataCreateTaskKeyParam
    const dataCreateTaskKey =
      raw === undefined || raw === null || String(raw).trim() === '' || Number.isNaN(Number(raw)) ? null : Number(raw)
    dispatch(ACTION_TYPES.INIT, { dataCreateTaskKey, periodTypeFromUrl })
  }, [dataCreateTaskKeyParam, dispatch, periodTypeFromUrl])

  return <>{children}</>
}

/**
 * @param {string} badgeColor
 * @returns {string}
 */
function _badgeTextClass(badgeColor) {
  switch (badgeColor) {
    case 'red':
      return 'red--text'
    case 'green':
    case 'lightGreen':
    case 'mint':
      return 'green--text'
    case 'orange':
    case 'yellow':
      return 'grey--text'
    case 'peach':
      return 'grey--text'
    case 'blue':
    case 'lightPurple':
      return 'blue1--text'
    default:
      return 'grey--text'
  }
}

/** 全体進捗カード凡例のドット色（Tailwind bg） */
function _progressLegendDotClass(badgeColor) {
  switch (badgeColor) {
    case 'red':
      return 'bg-red-500'
    case 'green':
    case 'lightGreen':
      return 'bg-green-500'
    case 'mint':
      return 'bg-emerald-400'
    case 'yellow':
      return 'bg-yellow-400'
    case 'blue':
    case 'lightPurple':
      return 'bg-blue-600'
    case 'orange':
      return 'bg-amber-500'
    case 'peach':
      return 'bg-rose-300'
    case 'sky':
      return 'bg-sky-400'
    case 'violet':
      return 'bg-violet-400'
    default:
      return 'bg-gray-400'
  }
}

/** 全体進捗凡例（6 区分、0 件も表示。verifying は検証待ちに合算） */
const MAPPING_PROGRESS_LEGEND_STATUSES = [
  'imported',
  'import-pending',
  'verification-error',
  'configuring',
  'verification-pending',
  'unset'
]

/** データ種行のステータスチップ背景（statusBadgeProps.badgeColor と対応） */
function _statusChipSurfaceClass(badgeColor) {
  switch (badgeColor) {
    case 'red':
      return 'bg-red-50 red--text'
    case 'green':
    case 'lightGreen':
      return 'bg-green-50 green--text'
    case 'mint':
      return 'bg-emerald-50 text-emerald-800'
    case 'blue':
    case 'lightPurple':
      return 'bg-blue-50 blue1--text'
    case 'orange':
    case 'yellow':
      return 'bg-amber-50 text-amber-900'
    case 'peach':
      return 'bg-rose-50 text-rose-900'
    case 'sky':
      return 'bg-sky-50 text-sky-700'
    case 'violet':
      return 'bg-violet-100 text-violet-800'
    default:
      return 'bg-gray-100 grey--text'
  }
}

/**
 * カード展開マップ用のデータ超種キー（SR-028 と同一オブジェクトに載せる）
 *
 * @param {string} groupKey
 * @returns {string}
 */
function _superSectionExpandKey(groupKey) {
  return `super:${groupKey}`
}

/**
 * IF02095 の data_classification を generic_data_kinds から表示名に解決する（名称が無い場合はコードを返す）
 *
 * @param {string} dataClassification
 * @param {import('./extra/types').GenericDataKindItem[] | undefined} kinds
 * @returns {string}
 */
function _displayNameForUploadDataKind(dataClassification, kinds) {
  const code = String(dataClassification ?? '').trim()
  if (!code) return ''
  const row = (kinds ?? []).find((k) => String(k?.genericDataKindCode ?? '').trim() === code)
  const name = String(row?.genericDataKindName ?? '').trim()
  return name || code
}

/** データ種別（子要素）の行コンポーネント */
const GenericDataKindRow = ({
  kind,
  status,
  isParentTaskUpdating,
  isParentTaskFixed,
  canUploadWrite,
  kindNavAllowed,
  taskKeyForRoutes,
  uploadFileList,
  statusMetaByDataKind
}) => {
  const code = kind.genericDataKindCode
  const { label: statusLabel, badgeColor } = statusBadgeProps(status)
  const mapperPath = `/data/analysisData/${encodeURIComponent(taskKeyForRoutes)}/dataMapper/${encodeURIComponent(code)}`
  const cardNavDisabled = isParentTaskUpdating || isParentTaskFixed || !canUploadWrite || !kindNavAllowed
  // メタ列（中央）の表示パーツを { user, date } の形で 1 つに集約する。
  // 優先順: status_meta_by_data_kind → upload_file_list 最新更新日時 → 「—」。
  // モックアップ chrofy_mockup/mapping_v2 shared-utils.js generateDataTypeTable .category-info-col 準拠。
  const rowMetaParts = (() => {
    const statusMeta = formatGenericKindStatusMeta(statusMetaByDataKind, code)
    if (statusMeta) return statusMeta
    const fallbackDate = formatGenericKindRowMeta(uploadFileList, code)
    if (fallbackDate) return { user: '', date: fallbackDate }
    return { user: '', date: '—' }
  })()

  return (
    <RouterLink
      to={cardNavDisabled ? '#' : mapperPath}
      aria-label={`${kind.genericDataKindName}のマッピング・検証画面を開く`}
      className={`flex items-center gap-3 border-t border-slate-200 px-4 py-3 no-underline transition-colors duration-150 first:border-t-0 ${
        cardNavDisabled
          ? 'pointer-events-none cursor-not-allowed opacity-60'
          : 'hover:bg-slate-100/35 active:bg-slate-100/55'
      }`}
      onClick={(e) => {
        if (cardNavDisabled) e.preventDefault()
      }}
    >
      <span className="flex min-w-0 flex-1 items-center gap-1">
        <span className="text-h6 font-bold blue1--text truncate">{kind.genericDataKindName}</span>
      </span>
      <span className="min-w-0 flex-1 text-center text-caption-2 grey--text">
        {rowMetaParts.user && <span>{rowMetaParts.user}</span>}
        {rowMetaParts.date && <span className={rowMetaParts.user ? 'ml-4' : ''}>{rowMetaParts.date}</span>}
      </span>
      <span className="flex w-[180px] shrink-0 items-center justify-end gap-2">
        <span
          className={`inline-block shrink-0 rounded-full px-2.5 py-1 text-caption-1 ${_statusChipSurfaceClass(badgeColor)}`}
        >
          {statusLabel}
        </span>
        <span className="text-caption-2 grey--text" aria-hidden>
          ›
        </span>
      </span>
    </RouterLink>
  )
}

/**
 * @param {Record<string, unknown>} props
 * @param {import('redux').Store} props.globalReduxStore アプリ全体の Redux（ローカル ReportDataUpdateProvider 内では useStore
 *   がローカルストアになるため Main から注入する）
 * @param {boolean} [props.isParent=true] 親法人のとき連結の財務行も表示。省略時は親扱い（単体子会社フィルタはしない）. Default is `true`
 * @returns {React.ReactElement}
 */
export function AnalysisDataContent(props) {
  const {
    dataCreateTaskKey: dataCreateTaskKeyParam,
    userSub,
    userName,
    pageTitle,
    corporationNumber,
    authConf,
    uploadableDataKindFiles,
    message: flashMessage,
    globalReduxStore: reduxStore,
    isParent = true
  } = props

  const location = useLocation()

  const showToast = useToast()

  const reportDispatch = useReportDataUpdateDispatch()
  const reportStore = useReportDataUpdateStore()

  /**
   * データファイル登録ガイドダウンロード処理
   *
   * @param {React.MouseEvent} e
   */
  const fileDownload = React.useCallback(
    (e) => {
      e.preventDefault()
      try {
        const bucketName = `chrofy-${process.env.REACT_APP_ENV}-${corporationNumber}`
        downloadGuide(
          bucketName,
          PathInfoList.Folder.uploadGuideFolder + PathInfoList.Objectkeyname.dataFileUploadGuide
        )
      } catch (error) {
        errorLog('E000001', userSub, viewId.analysisData, 'fileDownload', error)
      }
    },
    [corporationNumber, userSub]
  )

  /** 別画面へ遷移・同一タブの URL 変更・再読込時にインラインエラーを残さない（ストアはモジュール単位シングルトン） */
  React.useEffect(() => {
    reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS)
  }, [location.pathname, location.search, reportDispatch])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount 時のクリーンアップ。reportDispatch は再生成されるが実体は同一なので依存に含めない
  React.useEffect(() => {
    return () => {
      reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS)
    }
  }, [])

  const showPageActionError = React.useCallback(
    (message) => {
      const text = String(message ?? '').trim()
      if (!text) return
      reportDispatch(ACTION_TYPES.SET_INLINE_ERROR, { key: INLINE_ERROR_KEY_PAGE_ACTION, message: text })
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 0)
    },
    [reportDispatch]
  )

  const showDataUpdateError = React.useCallback(
    (message) => {
      const text = String(message ?? '').trim()
      if (!text) return
      reportDispatch(ACTION_TYPES.SET_INLINE_ERROR, { key: INLINE_ERROR_KEY_DATA_UPDATE, message: text })
    },
    [reportDispatch]
  )

  const showDataFixError = React.useCallback(
    (message) => {
      const text = String(message ?? '').trim()
      if (!text) return
      reportDispatch(ACTION_TYPES.SET_INLINE_ERROR, { key: INLINE_ERROR_KEY_DATA_FIX, message: text })
    },
    [reportDispatch]
  )

  const openDataUpdateModal = useDataUpdateConfirmModal({
    globalReduxStore: reduxStore,
    onPageError: showDataUpdateError,
    onExecute: async ({ userSub: sub, periodKind, tableKey }) => {
      reportDispatch(ACTION_TYPES.SET_ACTION_LOADING, true)
      try {
        const result = await postCreateData(reduxStore, { userSub: sub, periodKind, tableKey })
        if (result && typeof result === 'object' && /** @type {{ ok?: unknown }} */ (result).ok === false) {
          const msg = /** @type {{ message?: unknown }} */ (result).message
          showDataUpdateError(typeof msg === 'string' ? msg : '')
          return
        }
        await refreshReportTaskScreen(reduxStore, reportDispatch, tableKey)
        reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_DATA_UPDATE] })
        showToast('レポートの更新を開始しました。')
      } finally {
        reportDispatch(ACTION_TYPES.SET_ACTION_LOADING, false)
      }
    }
  })
  const openDataFixModal = useDataFixConfirmModal({
    globalReduxStore: reduxStore,
    onPageError: showDataFixError,
    onExecute: async ({ userSub: sub, userName: uname, key }) => {
      reportDispatch(ACTION_TYPES.SET_ACTION_LOADING, true)
      try {
        const result = await postFixData(reduxStore, { userSub: sub, userName: uname, key })
        const msg =
          result && typeof result === 'object' && result !== null && 'message' in result
            ? /** @type {{ message?: string | null }} */ (result).message
            : null
        if (msg != null && msg !== '') {
          showDataFixError(String(msg))
          return
        }
        await refreshReportTaskScreen(reduxStore, reportDispatch, key.Key)
        reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_DATA_FIX] })
        showToast('レポートデータを確定しました。')
      } finally {
        reportDispatch(ACTION_TYPES.SET_ACTION_LOADING, false)
      }
    }
  })
  const { openAccountingUploadErrorModal } = useAccountingUploadErrorModal()
  const { openDataImportGuideModal } = useDataImportGuideModal()

  const flashShownRef = React.useRef(false)
  const inlineErrorAnchorRef = React.useRef(null)

  const stateSlice = useReportDataUpdateSelector(
    (s) => ({
      screenStatus: s.screenStatus,
      loadError: s.loadError,
      inlineErrors: s.inlineErrors,
      dataCreateTask: s.dataCreateTask,
      dataCreateTaskKey: s.dataCreateTaskKey,
      periodTypeFromUrl: s.periodTypeFromUrl,
      genericDataKinds: s.genericDataKinds,
      statusByDataKind: s.statusByDataKind,
      statusMetaByDataKind: s.statusMetaByDataKind,
      accountingUploadTasks: s.accountingUploadTasks,
      uploadFileList: s.uploadFileList,
      allChrofyColumns: s.allChrofyColumns,
      mappingExportLoading: s.mappingExportLoading,
      mappingBulkLoading: s.mappingBulkLoading,
      validationBulkLoading: s.validationBulkLoading,
      actionLoading: s.actionLoading,
      cardsExpandedByKind: s.cardsExpandedByKind
    }),
    shallowEqual
  )

  const {
    screenStatus,
    loadError,
    inlineErrors,
    dataCreateTask,
    dataCreateTaskKey,
    periodTypeFromUrl,
    genericDataKinds,
    statusByDataKind,
    statusMetaByDataKind,
    accountingUploadTasks,
    uploadFileList,
    allChrofyColumns,
    mappingExportLoading,
    mappingBulkLoading,
    validationBulkLoading,
    actionLoading,
    cardsExpandedByKind
  } = stateSlice

  const operationHistory = dataCreateTask?.operationHistory ?? []

  // タスク取得後は API の periodType を優先し、未取得時のみ URL クエリを表示の期間解釈に使う
  const periodTypeEffective = String(dataCreateTask?.periodType ?? periodTypeFromUrl ?? '')

  const isParentTaskUpdating = dataCreateTask?.dataTaskStatus === dataCreateStatus.creating.value
  const isParentTaskFixed = dataCreateTask?.dataFixStatus === dataFixStatus.fix.value
  // タスクが「再更新待ち（recreate）」のときは、過去期間更新により再生成が必要な状態。
  // データ分類が更新待ち（import-pending / reimport-pending）でなくてもデータ更新を許可する（#3130 上司判断）。
  const isRecreateTask = dataCreateTask?.dataTaskStatus === dataCreateStatus.recreate.value

  /** Upload_AuthInfo.Auth_Conf.Regist（更新）が true のときのみアップロード系の書き込み操作を許可 */
  const canUploadWrite = Boolean(authConf?.Regist) && !isParentTaskFixed

  /** ファイル管理画面への遷移は更新（Regist）またはアップロードのみ（Upload）権限で許可 */
  const canFileManage = Boolean(authConf?.Regist || authConf?.Upload) && !isParentTaskFixed

  /**
   * クライアント側の API 呼び出しが進行中か（画面初期化/再取得・一括操作）。 サーバ側のデータ作成中（dataTaskStatus = creating）は含めない。 creating
   * はサーバ状態であって画面の通信中ではないため、これに依存すると リロードスピナーが creating の間ずっと回り続けてしまう（更新ボタン押下後の不具合）。
   */
  const isSystemBusy = React.useMemo(
    () => screenStatus !== SCREEN_STATUS.IDLE || mappingBulkLoading || validationBulkLoading || actionLoading,
    [screenStatus, mappingBulkLoading, validationBulkLoading, actionLoading]
  )

  /** 操作が制限されている（権限なし、または確定済）かどうか */
  const isActionRestricted = React.useMemo(
    () => !canUploadWrite || isParentTaskFixed,
    [canUploadWrite, isParentTaskFixed]
  )

  const bulkDataKindAllowed = Boolean(authConf?.Uploadable_DataKind?.Availability)
  const hasUploadableDataKindFiles = (uploadableDataKindFiles ?? []).length > 0
  // 一括操作はサーバ側データ作成中（creating）も不可（API（IF02097）が 400 を返すため先回りで非活性）。
  // isSystemBusy からは creating を外したので、ここで isParentTaskUpdating を明示的に加える。
  const baseBulkDisabled = React.useMemo(
    () =>
      isSystemBusy || isParentTaskUpdating || isActionRestricted || !bulkDataKindAllowed || !hasUploadableDataKindFiles,
    [isSystemBusy, isParentTaskUpdating, isActionRestricted, bulkDataKindAllowed, hasUploadableDataKindFiles]
  )
  // ベースファイル未指定（=有効アップロードファイルはあるが is_base_file_set が false）の判定。
  // 月次/四半期/年次すべてのタスクで、ファイルがあるのにベース未指定なら検証・更新を止める（バグ#3160）。
  // is_base_file_set は IF02095 が期間対応で解決済み（四半期/年次は対応月次タスクのベースを参照）。
  const baseFileMissing = !dataCreateTask?.isBaseFileSet && (uploadFileList ?? []).length > 0
  // 参照のみの処理のため、データ確定済／データ更新中でも実行できる。
  // 項目マスタ（IF02090）が取得できなかった場合のみ非活性にする（実装計画 5.3）。
  const mappingExportDisabled = (allChrofyColumns ?? []).length === 0 || mappingExportLoading

  const mappingBulkDisabled = baseBulkDisabled || isParentTaskFixed
  const validationBulkDisabled =
    baseBulkDisabled || isParentTaskFixed || !hasPendingValidationKinds(statusByDataKind) || baseFileMissing

  const progress = React.useMemo(
    () => calcOverallProgress(genericDataKinds, statusByDataKind),
    [genericDataKinds, statusByDataKind]
  )

  const uploadableKindCodeSet = React.useMemo(() => {
    const list = uploadableDataKindFiles ?? []
    return new Set(list.map((c) => String(c ?? '').trim()).filter((s) => s !== ''))
  }, [uploadableDataKindFiles])

  const groupedBySuperKind = React.useMemo(() => groupGenericDataKindsBySuperKind(genericDataKinds), [genericDataKinds])

  const displayedAccountingUploadTasks = React.useMemo(
    () => filterAccountingUploadTasksForParentContext(accountingUploadTasks, isParent),
    [accountingUploadTasks, isParent]
  )

  const showProgress = shouldShowProgressSummary(genericDataKinds)

  /** 進捗バーのセグメント定義 */
  const progressBarSegments = React.useMemo(() => {
    const imported = progress.countsByStatus.imported ?? 0
    const importPending =
      (progress.countsByStatus['import-pending'] ?? 0) + (progress.countsByStatus['reimport-pending'] ?? 0)
    const error = progress.countsByStatus['verification-error'] ?? 0
    const configuring = progress.countsByStatus.configuring ?? 0
    const verificationPending =
      (progress.countsByStatus['verification-pending'] ?? 0) + (progress.countsByStatus.verifying ?? 0)
    const unset = progress.countsByStatus.unset ?? 0

    return [
      { key: 'imported', count: imported, color: 'bg-green-500' },
      { key: 'importPending', count: importPending, color: 'bg-blue-600' },
      { key: 'error', count: error, color: 'bg-red-500' },
      { key: 'configuring', count: configuring, color: 'bg-amber-500' },
      { key: 'verificationPending', count: verificationPending, color: 'bg-violet-400' },
      { key: 'unset', count: unset, color: 'bg-gray-400' }
    ].filter((s) => s.count > 0)
  }, [progress])

  // 凡例の比率は進捗バーと同じ分母（全データ分類 − 1 件：汎用項目 GDK-0030 を除外）を使用し、
  // バーの各セグメント幅と凡例の % が一致するようにする。
  const legendDenominator = progress.denominator
  const legendCountByStatus = React.useMemo(
    () => ({
      imported: progress.countsByStatus.imported ?? 0,
      'import-pending':
        (progress.countsByStatus['import-pending'] ?? 0) + (progress.countsByStatus['reimport-pending'] ?? 0),
      'verification-error': progress.countsByStatus['verification-error'] ?? 0,
      configuring: progress.countsByStatus.configuring ?? 0,
      'verification-pending':
        (progress.countsByStatus['verification-pending'] ?? 0) + (progress.countsByStatus.verifying ?? 0),
      unset: progress.countsByStatus.unset ?? 0
    }),
    [progress]
  )

  const { openFixCancelModal } = useFixCancelModal({
    globalStore: reduxStore,
    operationUserSub: userSub,
    operationUserName: userName,
    corporationNumber,
    dataCreateTaskKey: dataCreateTaskKey ?? dataCreateTaskKeyParam,
    onExecute: async (request) => {
      // onExecute → onAfterSuccess（成功）/ onFailure（失敗）の順で必ずどちらかが走るため、
      // ここで busy を立て、両終端で必ず false に戻す。
      reportDispatch(ACTION_TYPES.SET_ACTION_LOADING, true)
      await postFixCancelData(reduxStore, request)
    },
    onAfterSuccess: async () => {
      const tk = reportStore.getState().dataCreateTaskKey ?? dataCreateTaskKeyParam
      await refreshReportTaskScreen(reduxStore, reportDispatch, tk)
      reportDispatch(ACTION_TYPES.SET_ACTION_LOADING, false)
      showToast('レポートデータの確定を解除しました。')
    },
    onFailure: (e) => {
      reportDispatch(ACTION_TYPES.SET_ACTION_LOADING, false)
      console.error(e)
      showPageActionError('データ確定解除処理でエラーが発生しました。')
    }
  })

  /** IF02095 でタスク画面を再取得し、ローカルストアへ反映する（一括操作・子画面から戻ったあとの表示同期用）。 */
  const _refreshTaskScreen = React.useCallback(async () => {
    const tk = reportStore.getState().dataCreateTaskKey ?? dataCreateTaskKeyParam
    await refreshReportTaskScreen(reduxStore, reportDispatch, tk)
  }, [dataCreateTaskKeyParam, reduxStore, reportDispatch, reportStore])

  const [historyPage, setHistoryPage] = React.useState(1)
  const historyPerPage = 10

  const [refreshFabLoading, setRefreshFabLoading] = React.useState(false)

  const handleFinancialListRefresh = React.useCallback(async () => {
    try {
      await _refreshTaskScreen()
      reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_PAGE_ACTION] })
    } catch (e) {
      console.error(e)
      errorLog('E000001', userSub, viewId.analysisData, 'fetchGenericTaskScreen', e)
      showPageActionError('財務データ一覧の更新でエラーが発生しました。')
    }
  }, [_refreshTaskScreen, reportDispatch, showPageActionError, userSub])

  const handleRefreshFabClick = React.useCallback(async () => {
    setRefreshFabLoading(true)
    try {
      await handleFinancialListRefresh()
    } finally {
      setRefreshFabLoading(false)
    }
  }, [handleFinancialListRefresh])

  React.useEffect(() => {
    document.title = pageTitle + titleName.common
  }, [pageTitle])

  React.useEffect(() => {
    const mountFlash = flashMessage
    const mountTaskKeyParam = dataCreateTaskKeyParam
    window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ初期取得（component.mdc §3 Mount Only）
    ;(async () => {
      try {
        reportDispatch(ACTION_TYPES.LOAD_START)
        const tk = reportStore.getState().dataCreateTaskKey ?? mountTaskKeyParam
        if (tk === null || tk === undefined || String(tk).trim() === '') {
          reportDispatch(ACTION_TYPES.LOAD_ERROR, 'タスクが指定されていません。')
          return
        }
        // IF02095（画面データ）と IF02090（全 CHROFY 項目）を並列取得。
        // IF02090 はマッピング設定 Excel ダウンロード専用のため、
        // 失敗しても画面表示は継続し、ダウンロードボタンのみ非活性にする（実装計画 5.3）。
        const [screen, chrofyColumns] = await Promise.all([
          fetchGenericTaskScreen(reduxStore, tk),
          fetchAllChrofyColumns(reduxStore).catch((err) => {
            console.error(err)
            return []
          })
        ])
        reportDispatch(ACTION_TYPES.INIT_SUCCESS, screen)
        reportDispatch(ACTION_TYPES.SET_ALL_CHROFY_COLUMNS, chrofyColumns)
        if (mountFlash && !flashShownRef.current) {
          showToast(String(mountFlash))
          flashShownRef.current = true
        }
      } catch (err) {
        console.error(err)
        reportDispatch(ACTION_TYPES.LOAD_ERROR, err)
      }
    })()
  }, [])

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname, location.search])

  const handleRetryLoad = React.useCallback(() => {
    ;(async () => {
      try {
        reportDispatch(ACTION_TYPES.LOAD_START)
        const tk = reportStore.getState().dataCreateTaskKey ?? dataCreateTaskKeyParam
        if (tk === null || tk === undefined || String(tk).trim() === '') {
          reportDispatch(ACTION_TYPES.LOAD_ERROR, 'タスクが指定されていません。')
          return
        }
        const screen = await fetchGenericTaskScreen(reduxStore, tk)
        reportDispatch(ACTION_TYPES.INIT_SUCCESS, screen)
      } catch (err) {
        console.error(err)
        reportDispatch(ACTION_TYPES.LOAD_ERROR, err)
      }
    })()
  }, [dataCreateTaskKeyParam, reduxStore, reportDispatch, reportStore])

  const handleExpandAllCards = React.useCallback(() => {
    const next = {}
    for (const g of groupedBySuperKind) {
      next[_superSectionExpandKey(g.groupKey)] = true
    }
    reportDispatch(ACTION_TYPES.SET_CARDS_EXPANDED, next)
  }, [groupedBySuperKind, reportDispatch])

  const handleCollapseAllCards = React.useCallback(() => {
    reportDispatch(ACTION_TYPES.SET_CARDS_EXPANDED, {})
  }, [reportDispatch])

  const handleToggleCardExpanded = React.useCallback(
    (kindCode) => {
      reportDispatch(ACTION_TYPES.TOGGLE_CARD_EXPANDED, { kindCode })
    },
    [reportDispatch]
  )

  /**
   * マッピング設定を Excel でダウンロードする（実装計画 5.2）。
   *
   * 項目一覧（IF02090）は初期表示で取得済みのため、ここでは マッピング（IF02085）と判定ルール（IF02092）のみを並列取得する。 参照のみの処理であり、データ確定済／更新中でも実行できる。
   */
  const handleDownloadMappingSetting = React.useCallback(async () => {
    const tk = reportStore.getState().dataCreateTaskKey ?? dataCreateTaskKeyParam
    if (tk === null || tk === undefined) {
      showPageActionError('処理を実行できません。')
      return
    }
    reportDispatch(ACTION_TYPES.SET_MAPPING_EXPORT_LOADING, true)
    try {
      const [mappings, judgmentRules, conversionTableList] = await Promise.all([
        fetchAllMappingSettings(reduxStore, tk),
        // 判定ルールが取得できなくても、マッピング内容の出力自体は継続する
        fetchJudgmentRules(reduxStore).catch((err) => {
          console.error(err)
          return []
        }),
        // 変換表名の解決用。取得できない場合は変換表キーがそのまま出力される
        fetchConversionTableList(reduxStore).catch((err) => {
          console.error(err)
          return []
        })
      ])
      const state = reportStore.getState()
      const periodType = state.dataCreateTask?.periodType ?? periodTypeFromUrl
      const rows = buildMappingExportRows({
        genericDataKinds: state.genericDataKinds,
        allChrofyColumns: state.allChrofyColumns,
        mappings,
        judgmentRules,
        conversionTableList,
        // 四半期タスクでは対象四半期外の項目を出力しない（マッピング設定画面と同じ絞り込み）
        currentQuarter: extractQuarterLabel(periodType, state.dataCreateTask?.periodLabel)
      })
      await exportMappingToExcel(rows, {
        periodTypeLabel: formatPeriodTypeLabel(periodType),
        periodLabel: formatPeriodLabelForHeader(state.dataCreateTask?.periodLabel),
        exportedAt: formatTaskScreenDateTime(new Date())
      })
      // 成功トーストは出さない: ファイルがダウンロードされること自体が完了の合図になるため
      reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_PAGE_ACTION] })
    } catch (e) {
      console.error(e)
      errorLog('E000001', userSub, viewId.analysisData, 'handleDownloadMappingSetting', e)
      showPageActionError('マッピング設定のダウンロードでエラーが発生しました。')
    } finally {
      reportDispatch(ACTION_TYPES.SET_MAPPING_EXPORT_LOADING, false)
    }
  }, [dataCreateTaskKeyParam, periodTypeFromUrl, reduxStore, reportDispatch, reportStore, showPageActionError, userSub])

  const handleMappingBulk = React.useCallback(async () => {
    // 操作対象タスクは常にローカルストアのキー（URL 同期後）を信頼する
    const tk = reportStore.getState().dataCreateTaskKey ?? dataCreateTaskKeyParam
    if (tk === null || tk === undefined) {
      showPageActionError('処理を実行できません。')
      return
    }
    reportDispatch(ACTION_TYPES.SET_MAPPING_BULK_LOADING, true)
    try {
      await postCopyPreviousMappingBulk(reduxStore, tk)
      reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_PAGE_ACTION] })
      showToast('マッピング一括設定が完了しました。')
    } catch (e) {
      console.error(e)
      errorLog('E000001', userSub, viewId.analysisData, 'postCopyPreviousMappingBulk', e)
      showPageActionError('マッピング一括設定でエラーが発生しました。')
      return
    } finally {
      reportDispatch(ACTION_TYPES.SET_MAPPING_BULK_LOADING, false)
    }
    // 一括成功後は初期表示と同じフローで画面を再初期化し、最新状態を反映する
    try {
      reportDispatch(ACTION_TYPES.LOAD_START)
      const screen = await fetchGenericTaskScreen(reduxStore, tk)
      reportDispatch(ACTION_TYPES.INIT_SUCCESS, screen)
    } catch (err) {
      console.error(err)
      reportDispatch(ACTION_TYPES.LOAD_ERROR, err)
    }
  }, [dataCreateTaskKeyParam, reduxStore, reportDispatch, reportStore, showPageActionError, showToast, userSub])

  const handleValidationBulk = React.useCallback(async () => {
    // ボタン押下直後の最新状態でチェックし、API（IF02097）の creating 拒否と整合させる
    const rs = reportStore.getState()
    const pre = validateDataValidationBulkPreconditions({
      statusByDataKind: rs.statusByDataKind,
      dataTaskStatus: rs.dataCreateTask?.dataTaskStatus
    })
    if (!pre.ok) {
      showPageActionError(pre.message ?? MSG_VALIDATION_BULK_NO_PENDING)
      return
    }

    const tk = reportStore.getState().dataCreateTaskKey ?? dataCreateTaskKeyParam
    if (tk === null || tk === undefined) {
      showPageActionError('処理を実行できません。')
      return
    }
    // 検証待ちデータ種別が存在する前提で一括検証 API を叩き、完了後に画面を再初期化する
    reportDispatch(ACTION_TYPES.SET_VALIDATION_BULK_LOADING, true)
    try {
      await postStartGenericDataValidationBulk(reduxStore, tk)
      reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_PAGE_ACTION] })
      showToast('データ検証一括を開始しました。')
    } catch (e) {
      console.error(e)
      errorLog('E000001', userSub, viewId.analysisData, 'postStartGenericDataValidationBulk', e)
      showPageActionError('データ検証一括でエラーが発生しました。')
      return
    } finally {
      reportDispatch(ACTION_TYPES.SET_VALIDATION_BULK_LOADING, false)
    }
    // 一括成功後は初期表示と同じフローで画面を再初期化し、最新状態を反映する
    try {
      reportDispatch(ACTION_TYPES.LOAD_START)
      const screen = await fetchGenericTaskScreen(reduxStore, tk)
      reportDispatch(ACTION_TYPES.INIT_SUCCESS, screen)
    } catch (err) {
      console.error(err)
      reportDispatch(ACTION_TYPES.LOAD_ERROR, err)
    }
  }, [
    accountingUploadTasks,
    dataCreateTask,
    dataCreateTaskKeyParam,
    genericDataKinds,
    reduxStore,
    reportDispatch,
    reportStore,
    showPageActionError,
    showToast,
    statusByDataKind,
    uploadFileList,
    userSub
  ])

  const handleDataFixClick = React.useCallback(() => {
    reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_DATA_FIX] })
    openDataFixModal()
  }, [openDataFixModal, reportDispatch])

  const handleDataUpdateClick = React.useCallback(() => {
    reportDispatch(ACTION_TYPES.CLEAR_INLINE_ERRORS, { keys: [INLINE_ERROR_KEY_DATA_UPDATE] })
    // ボタン押下直後のファイル登録状況・検証状態でチェックし、確認モーダル内の判定と整合させる
    const rs = reportStore.getState()
    const pre = validateDataUpdatePreconditions({
      statusByDataKind: rs.statusByDataKind,
      dataTaskStatus: rs.dataCreateTask?.dataTaskStatus,
      dataFixStatus: rs.dataCreateTask?.dataFixStatus
    })
    if (!pre.ok) {
      reportDispatch(ACTION_TYPES.SET_INLINE_ERROR, {
        key: INLINE_ERROR_KEY_DATA_UPDATE,
        message: pre.message ?? ''
      })
      return
    }
    // 四半期/年次で最新月次が更新済以上かつファイル0件のときは全ファイルアップロード完了チェックを免除（バグ#3136）。
    // ボタン非活性判定（fileLessUpdateAllowed）と同一条件で揃える。
    const rsPeriodType = String(rs.dataCreateTask?.periodType ?? rs.periodTypeFromUrl ?? '')
    const rsFileLessUpdateAllowed = isFileLessUpdateAllowed(
      rsPeriodType,
      rs.dataCreateTask?.closestMonthlyDataCreated,
      rs.uploadFileList
    )
    if (!rsFileLessUpdateAllowed && !allUploadFilesAreUploaded(rs.uploadFileList)) {
      reportDispatch(ACTION_TYPES.SET_INLINE_ERROR, {
        key: INLINE_ERROR_KEY_DATA_UPDATE,
        message: MSG_DATA_UPDATE_FILES_NOT_ALL_UPLOADED
      })
      return
    }
    openDataUpdateModal()
  }, [openDataUpdateModal, reportDispatch, reportStore])

  /** 「ファイルを管理」ボタンの表示内容 */
  const fileManagementButton = React.useMemo(() => {
    const taskKey = String(dataCreateTaskKey ?? dataCreateTaskKeyParam ?? '')
    const commonClass =
      'btn btn-link rounded-lg px-5 py-2 !pt-2 !pb-2 min-h-0 inline-flex items-center justify-center gap-1.5 text-caption-1 no-underline shrink-0'
    const icon = <FolderRoundedIcon fontSize="small" className="shrink-0" aria-hidden />

    if (canFileManage) {
      return (
        <RouterLink to={`/data/analysisData/${encodeURIComponent(taskKey)}/fileManagement`} className={commonClass}>
          {icon}
          ファイルを管理
        </RouterLink>
      )
    }

    return (
      <span
        role="link"
        aria-disabled="true"
        aria-label="ファイルを管理（権限により利用できません）"
        className={`${commonClass} cursor-not-allowed opacity-60 pointer-events-none`}
      >
        {icon}
        ファイルを管理
      </span>
    )
  }, [canFileManage, dataCreateTaskKey, dataCreateTaskKeyParam])

  const taskKeyForRoutes = String(dataCreateTaskKey ?? dataCreateTaskKeyParam ?? '')

  // isSystemBusy はサーバ側データ作成中（creating）を含まないため、
  // 一括操作ボタンと同様に isParentTaskUpdating を明示的に加えて更新中の再実行を防ぐ。
  const dataUpdateButtonDisabled =
    dataCreateTask?.dataFixStatus === dataFixStatus.fix.value ||
    !authConf?.Data?.Create ||
    !canUploadWrite ||
    isSystemBusy ||
    isParentTaskUpdating ||
    baseFileMissing

  const dataUpdatePreconditions = React.useMemo(
    () =>
      validateDataUpdatePreconditions({
        statusByDataKind,
        dataTaskStatus: dataCreateTask?.dataTaskStatus,
        dataFixStatus: dataCreateTask?.dataFixStatus
      }),
    [statusByDataKind, dataCreateTask?.dataTaskStatus, dataCreateTask?.dataFixStatus]
  )

  const hasUpdatePendingKinds = React.useMemo(() => {
    for (const s of Object.values(statusByDataKind ?? {})) {
      const v = String(s)
      if (v === 'import-pending' || v === 'reimport-pending') {
        return true
      }
    }
    return false
  }, [statusByDataKind])

  const allUploadFilesUploaded = React.useMemo(() => allUploadFilesAreUploaded(uploadFileList), [uploadFileList])

  /**
   * 四半期/年次タスクで、対象期間の最新月次が更新済以上（closestMonthlyDataCreated）かつ アップロードファイルが 0 件のとき、ファイル無しでもデータ更新を許可する（バグ#3136）。
   * このとき「更新待ち分類なし」「ファイル未アップロード/未完了」のゲートを緩和する。 権限・確定済・システムBusy・検証中・作成中などのガードは緩和しない。
   */
  const fileLessUpdateAllowed = React.useMemo(
    () => isFileLessUpdateAllowed(periodTypeEffective, dataCreateTask?.closestMonthlyDataCreated, uploadFileList),
    [periodTypeEffective, dataCreateTask?.closestMonthlyDataCreated, uploadFileList]
  )

  const isDataUpdateActionDisabled =
    dataUpdateButtonDisabled ||
    (!fileLessUpdateAllowed && !hasUpdatePendingKinds && !isRecreateTask) ||
    (!fileLessUpdateAllowed && !allUploadFilesUploaded)

  const dataUpdateSubNote = React.useMemo(() => {
    // 当タスク自身がデータ更新中は他の注記より優先して表示する
    if (isParentTaskUpdating) return '※ データ更新中です。'
    if (baseFileMissing) return '※ ベースファイルが未登録のため、データ更新できません'
    if (dataUpdateButtonDisabled) return null
    // ファイル無し更新が許可されるケースは「更新待ち分類なし」「ファイル未完了」の注記を出さない
    if (fileLessUpdateAllowed) return null
    if (!hasUpdatePendingKinds && !isRecreateTask) return '※ 更新待ちのデータがありません。'
    if (!allUploadFilesUploaded) return `※ ${MSG_DATA_UPDATE_FILES_NOT_ALL_UPLOADED}`
    if (!dataUpdatePreconditions.ok && dataUpdatePreconditions.message) {
      return `※ ${dataUpdatePreconditions.message}`
    }
    return null
  }, [
    allUploadFilesUploaded,
    baseFileMissing,
    dataUpdateButtonDisabled,
    fileLessUpdateAllowed,
    hasUpdatePendingKinds,
    isParentTaskUpdating,
    isRecreateTask,
    dataUpdatePreconditions
  ])

  const dataUpdatePrimaryButtonClassName =
    'btn inline-flex w-full min-h-[42px] items-center justify-center gap-1.5 rounded-lg !py-2.5 px-4 text-white ' +
    (isDataUpdateActionDisabled
      ? 'cursor-not-allowed !border-slate-200 !bg-slate-100 !text-slate-500'
      : '!border-[#297ad8] !bg-[#297ad8] hover:!border-[#1c65ba] hover:!bg-[#1c65ba]')

  const dataFixButtonDisabled = !authConf?.Data?.Lock || !canUploadWrite || isSystemBusy

  const dataFixCancelButtonDisabled = !authConf?.Data?.Unlock || isSystemBusy

  if (screenStatus === SCREEN_STATUS.LOADING) {
    return (
      <main className="page-container">
        <section className="page-content">
          <BackToTaskListLink />
          <div className="centered-container py-16">
            <LoadingMessage />
          </div>
        </section>
      </main>
    )
  }

  if (screenStatus === SCREEN_STATUS.ERROR) {
    return (
      <main className="page-container">
        <section className="page-content">
          <BackToTaskListLink />
          <div className="centered-container py-10">
            <div className="box-xy-bordered border-blue1 p-8 max-w-2xl mx-auto">
              <p className="text-body red--text text-center mb-6">{loadError ?? 'データの取得に失敗しました。'}</p>
              <div className="flex justify-center">
                <button
                  type="button"
                  className="btn btn-link px-8 py-2 !bg-[#297ad8] hover:!bg-[#1c65ba]"
                  onClick={handleRetryLoad}
                >
                  再読み込み
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    )
  }

  const pageActionMessage = inlineErrors[INLINE_ERROR_KEY_PAGE_ACTION]

  const dataUpdateInlineError = inlineErrors[INLINE_ERROR_KEY_DATA_UPDATE]
  // 他期間更新中の案内はエラー（赤文字）ではなく注記スタイル（黒文字・※付き）で表示する
  const dataUpdateInlineIsInfo = dataUpdateInlineError === MSG_DATA_UPDATE_OTHER_PERIOD_CREATING

  return (
    <main className="page-container">
      <section className="page-content">
        <BackToTaskListLink />
        <div className="mx-auto px-6">
          <header className="page-header flex justify-center py-1 pt-8 mb-2">
            <h1 className="page-header__name text-h1 grey--text py-8">{pageTitle}</h1>
          </header>

          <div className="text-center mb-5">
            <p className="text-body grey--text mb-2">
              データファイルのアップロードとマッピングおよびデータの更新を行います。
            </p>
            <p className="text-body grey--text mb-2">
              データ更新を行うと、各ダッシュボードのグラフのデータが更新されます。
            </p>
            <p className="text-body grey--text">
              <Link underline="none" href="#" onClick={fileDownload}>
                データアップロードガイド(PDFファイル)
              </Link>
              をご覧ください。
            </p>
          </div>
        </div>

        <div className="page-body">
          <div className="centered-container">
            <div className="sub-title-area flex justify-center items-center py-4 mb-2 flex-wrap gap-3">
              <h3 className="text-h4 font-bold m-0 leading-none">更新対象データ：</h3>
              <table className="base-table ml-5 m-0 w-auto table-fixed align-middle">
                <tbody className="text-h5 text-center">
                  <tr>
                    <td className="blue1 white--text align-middle w-[150px]">種類</td>
                    <td className="border-blue1 align-middle w-[150px]">
                      {formatPeriodTypeLabel(periodTypeEffective)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="base-table ml-5 m-0 w-auto table-fixed align-middle">
                <tbody className="text-h5 text-center">
                  <tr>
                    <td className="blue1 white--text align-middle w-[150px]">
                      {periodTypeEffective === PERIOD_TYPE_QUARTER ? '四半期' : '期間'}
                    </td>
                    <td className="border-blue1 align-middle w-[150px]">
                      {formatPeriodLabelForHeader(dataCreateTask?.periodLabel)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {pageActionMessage && (
              <div id="analysisDataPageErrors" className="mb-3 w-full text-left" role="alert">
                {String(pageActionMessage)
                  .split('\n')
                  .filter((line) => line.trim() !== '')
                  .map((line, i) => (
                    <p key={i} className="text-body red--text m-0 mb-1 text-left last:mb-0">
                      {line}
                    </p>
                  ))}
              </div>
            )}

            <div ref={inlineErrorAnchorRef} className="min-h-0">
              {isParentTaskFixed && (
                <p className="text-body red--text m-0 mb-3" role="alert">
                  データ確定済のため、設定の変更や更新はできません。
                </p>
              )}
            </div>

            <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl mt-4 pt-10 pb-16 px-16 border-blue1 w-full max-w-none">
              <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                ファイルアップロード、データマッピング＆データ検証
              </h2>

              <div className="box-xy-bordered border-blue2-lighten-20 rounded-xl p-6 mb-8 mt-5">
                <h3 className="text-h5 font-bold mb-3">■ファイルアップロード</h3>
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <p className="text-caption-1 grey--text m-0">
                      「ファイルを管理」ボタンをクリックし、データファイルをアップロードしてください。
                      <br />
                      既にアップロードしたファイルに関する設定の変更をする場合も、「ファイルを管理」ボタンから行ってください。
                      <button
                        type="button"
                        aria-label="データ取り込みの手順を表示"
                        className="inline-flex items-center justify-center border-none bg-transparent p-0 text-[#297ad8] hover:opacity-80 align-middle ml-1"
                        onClick={openDataImportGuideModal}
                      >
                        <InfoOutlinedIcon fontSize="small" aria-hidden />
                      </button>
                    </p>
                  </div>
                  <div className="shrink-0">{fileManagementButton}</div>
                </div>
                {baseFileMissing && (
                  <div
                    className="p-4 bg-yellow-50 border border-yellow-200 rounded text-caption-2 text-yellow-800 mb-3"
                    role="alert"
                  >
                    ⚠️ ベースファイルが未登録のため、データ更新できません。
                  </div>
                )}
                {(uploadFileList ?? []).length === 0 ? (
                  <div
                    className="p-4 bg-yellow-50 border border-yellow-200 rounded text-caption-2 text-yellow-800 mb-3"
                    role="alert"
                  >
                    ⚠️{' '}
                    {fileLessUpdateAllowed
                      ? 'アップロードされたファイルはありません。ファイルをアップロードしない場合、月次の更新済データを基にデータ更新を実行します。'
                      : 'アップロードされたファイルがありません。「ファイルを管理」ボタンからファイルをアップロードしてください。'}
                  </div>
                ) : (
                  <div>
                    <h4 className="text-h6 font-bold mb-2">アップロード済みファイル</h4>
                    <div className="overflow-x-auto border border-blue2-lighten-20 rounded-none">
                      <table className="base-table table-fixed w-full min-w-[1100px]">
                        <colgroup>
                          <col className="w-[14%]" />
                          <col className="w-[28%]" />
                          <col className="w-[9%]" />
                          <col className="w-[6%]" />
                          <col className="w-[11%]" />
                          <col className="w-[17%]" />
                          <col className="w-[15%]" />
                        </colgroup>
                        <thead className="base-table-header text-h7">
                          <tr>
                            <th className="min-w-0 whitespace-nowrap align-middle">ファイル種別</th>
                            <th className="min-w-0 whitespace-nowrap align-middle">ファイル名</th>
                            <th className="min-w-0 whitespace-nowrap text-center align-middle">ファイルサイズ</th>
                            <th className="min-w-0 whitespace-nowrap text-center align-middle">行数</th>
                            <th className="min-w-0 whitespace-nowrap align-middle">最終更新者</th>
                            <th className="min-w-0 whitespace-nowrap align-middle">最終更新日時</th>
                            <th className="min-w-0 whitespace-nowrap text-center align-middle">ステータス</th>
                          </tr>
                        </thead>
                        <tbody className="text-caption-2">
                          {(uploadFileList ?? []).map((row) => {
                            const uploadStatus = uploadFileListStatusBadgeProps(row.status)
                            return (
                              <tr
                                key={`${String(row.fileName ?? '')}-${String(row.updateDateTime ?? '')}-${String(row.dataClassification ?? '')}-${uploadStatus.rawStatus}`}
                                className="border_black"
                              >
                                <td className="min-w-0 whitespace-nowrap align-middle">
                                  {_displayNameForUploadDataKind(row.dataClassification, genericDataKinds)}
                                </td>
                                <td className="min-w-0 align-middle">
                                  <CustomTooltip title={row.fileName} placement="top">
                                    {(ref) => (
                                      <span ref={ref} className="block max-w-full truncate">
                                        {row.fileName}
                                      </span>
                                    )}
                                  </CustomTooltip>
                                </td>
                                <td className="min-w-0 text-center text-caption-2 grey--text align-middle whitespace-nowrap">
                                  {formatBytes(row.fileSize)}
                                </td>
                                <td className="min-w-0 text-center text-caption-2 grey--text align-middle whitespace-nowrap">
                                  {formatUploadFileListNumericCell(row.rowCount)}
                                </td>
                                <td className="min-w-0 break-all text-caption-2 grey--text align-middle">
                                  {formatUploadFileListUserCell(row.lastUpdateUserName)}
                                </td>
                                <td className="min-w-0 whitespace-nowrap align-middle">
                                  {formatUploadFileListDateTime(row.updateDateTime)}
                                </td>
                                <td className="min-w-0 whitespace-nowrap text-center align-middle">
                                  <span
                                    className={`inline-flex min-h-0 max-w-full items-center justify-center whitespace-nowrap rounded-lg px-2.5 py-1 text-caption-1 !leading-tight ${_statusChipSurfaceClass(uploadStatus.badgeColor)}`}
                                  >
                                    {uploadStatus.label}
                                  </span>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="box-xy-bordered border-blue2-lighten-20 rounded-xl p-6 mb-8"
                data-testid="mapping-section"
              >
                <h3 className="text-h5 font-bold mb-3">■データマッピング設定＆データ検証</h3>
                <div className="flex items-center justify-between gap-4 mb-3">
                  <p className="text-caption-1 grey--text m-0 flex-1">
                    データカテゴリ内の各データ項目を選択し、アップロードファイルの該当項目との紐づけと検証を行ってください。
                    <br />
                    前回設定済みのマッピングルールをまとめて設定・検証する場合には、「マッピング一括設定」および「データ検証一括実行」ボタンを押してください。
                  </p>
                  <div className="flex flex-wrap items-center justify-end gap-3 shrink-0">
                    <SpinnerButton
                      className="btn inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-lg border border-blue1 !bg-blue-50 !py-2.5 px-4 blue1--text hover:!bg-blue-100 disabled:!cursor-not-allowed disabled:!border-slate-300 disabled:!bg-slate-200 disabled:!text-slate-600"
                      disabled={mappingBulkDisabled}
                      name={
                        <span className="inline-flex items-center gap-1.5">
                          <SwapHorizIcon fontSize="small" className="shrink-0 text-inherit" aria-hidden />
                          マッピング一括設定
                        </span>
                      }
                      onClick={handleMappingBulk}
                    />
                    <SpinnerButton
                      className="btn btn-link inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-lg !bg-[#297ad8] !py-2.5 px-4 text-white hover:!border-[#1c65ba] hover:!bg-[#1c65ba] disabled:!cursor-not-allowed disabled:!border-[rgba(116,118,166,0.35)] disabled:!bg-[rgba(116,118,166,0.35)] disabled:!text-white"
                      disabled={validationBulkDisabled}
                      name={
                        <span className="inline-flex items-center gap-1.5 text-white">
                          <CheckIcon fontSize="small" className="shrink-0 text-inherit" aria-hidden />
                          データ検証一括実行
                        </span>
                      }
                      onClick={handleValidationBulk}
                    />
                  </div>
                </div>
                {isParentTaskUpdating && (
                  <p className="text-caption-2 red--text m-0 mb-3 text-right">
                    {MSG_VALIDATION_DISABLED_TASK_CREATING}
                  </p>
                )}
                {showProgress && (
                  <>
                    <div
                      className="box-xy-bordered border-blue2-lighten-20 rounded-xl mb-4 p-5"
                      data-testid="progress-section"
                    >
                      <h4 className="text-h6 font-bold mb-4">全体進捗状況</h4>
                      <div className="mb-4">
                        <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-200" role="presentation">
                          {progressBarSegments.map((seg) => (
                            <div
                              key={seg.key}
                              className={`h-full min-w-0 shrink ${seg.color}`}
                              style={{ flex: `${seg.count} 1 0%` }}
                            />
                          ))}
                        </div>
                      </div>
                      <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-2 p-0">
                        {MAPPING_PROGRESS_LEGEND_STATUSES.map((st) => {
                          const cnt = legendCountByStatus[st] ?? 0
                          const { label, badgeColor } = statusBadgeProps(st)
                          const pct = legendDenominator === 0 ? null : Math.round((cnt / legendDenominator) * 100)
                          return (
                            <li key={st} className="text-caption-2 flex items-center gap-2">
                              <span
                                className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${_progressLegendDotClass(badgeColor)}`}
                                aria-hidden
                              />
                              <span className="grey--text">
                                {`${label}: ${cnt}${pct !== null ? ` (${pct}%)` : ''}`}
                              </span>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                    <div className="mb-4 flex justify-end text-caption-1 text-slate-600">
                      <button
                        type="button"
                        className="m-0 min-h-0 cursor-pointer border-0 bg-transparent p-0 text-inherit underline-offset-2 transition-colors hover:text-blue1 hover:underline focus-visible:outline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                        onClick={handleExpandAllCards}
                      >
                        すべて展開
                      </button>
                      <span className="mx-2 shrink-0 text-slate-300 select-none" aria-hidden>
                        |
                      </span>
                      <button
                        type="button"
                        className="m-0 min-h-0 cursor-pointer border-0 bg-transparent p-0 text-inherit underline-offset-2 transition-colors hover:text-blue1 hover:underline focus-visible:outline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                        onClick={handleCollapseAllCards}
                      >
                        すべて折りたたむ
                      </button>
                    </div>
                  </>
                )}

                <div className="flex flex-col gap-3">
                  {groupedBySuperKind.map((group) => {
                    const expandKey = _superSectionExpandKey(group.groupKey)
                    const expanded = cardsExpandedByKind[expandKey] ?? false
                    const gProgress = calcOverallProgress(group.kinds, statusByDataKind, {
                      excludeGenericItem: false
                    })
                    const gDenom = gProgress.denominator
                    const gImportedCnt = gProgress.countsByStatus.imported ?? 0
                    const gErrCnt = gProgress.countsByStatus['verification-error'] ?? 0
                    const gPct = Math.min(100, Math.max(0, Math.round(gProgress.percent)))
                    return (
                      <div
                        key={group.groupKey}
                        className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                      >
                        <button
                          type="button"
                          className="w-full border-0 bg-slate-100 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-slate-200/45 focus-visible:outline focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                          onClick={() => handleToggleCardExpanded(expandKey)}
                        >
                          <div className="grid w-full min-w-0 grid-cols-1 items-center gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(7rem,30%)] sm:gap-x-3 sm:gap-y-0">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="shrink-0 text-caption-2 text-slate-600" aria-hidden>
                                {expanded ? '▼' : '▶'}
                              </span>
                              <span className="truncate text-h6 font-bold text-slate-700">{group.superKindName}</span>
                            </div>
                            <div className="flex shrink-0 flex-wrap items-center justify-start gap-x-2 gap-y-0.5 sm:justify-end">
                              {gImportedCnt > 0 ? (
                                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-caption-1 font-medium bg-emerald-50 text-emerald-800">
                                  済{gImportedCnt}
                                </span>
                              ) : null}
                              {gErrCnt > 0 ? (
                                <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-caption-1 red--text">
                                  エラー{gErrCnt}
                                </span>
                              ) : null}
                              <span className="whitespace-nowrap text-caption-2 text-slate-500">{gDenom}分類</span>
                            </div>
                            <div
                              className="min-h-[6px] min-w-0 overflow-hidden rounded-full bg-gray-200"
                              role="progressbar"
                              aria-valuenow={gPct}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              <div className="h-[6px] rounded-full bg-blue-800" style={{ width: `${gPct}%` }} />
                            </div>
                          </div>
                        </button>
                        {expanded && (
                          <div className="border-t border-slate-200">
                            {group.kinds.map((kind) => (
                              <GenericDataKindRow
                                key={kind.genericDataKindCode}
                                kind={kind}
                                status={statusByDataKind[kind.genericDataKindCode]}
                                isParentTaskUpdating={isParentTaskUpdating}
                                isParentTaskFixed={isParentTaskFixed}
                                canUploadWrite={canUploadWrite}
                                kindNavAllowed={uploadableKindCodeSet.has(
                                  String(kind.genericDataKindCode ?? '').trim()
                                )}
                                taskKeyForRoutes={taskKeyForRoutes}
                                uploadFileList={uploadFileList}
                                statusMetaByDataKind={statusMetaByDataKind}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/*
                 * マッピング設定 Excel ダウンロード（実装計画 Phase 6）。
                 * 一覧を確認した後に出力するという操作順に合わせ、セクション末尾の右下に配置する。
                 * 参照のみの処理のため、データ確定済／データ更新中でも非活性にしない。
                 */}
                <div className="mt-5 flex flex-col items-end gap-1 border-t border-slate-200 pt-4">
                  {/*
                   * 見た目・アイコンは区分マスター設定／変換表設定のエクスポートボタンに合わせる
                   * （conversionTableInfo/components/ConversionTableToolbar.js の exportButtonClass）。
                   *
                   * 処理中の表示はボタン幅を変えないことを優先する:
                   *
                   * - SpinnerButton はラベルを「処理中」へ差し替えるため幅が縮む → 使わない
                   * - スピナーをラベル横に追加すると幅が広がる → アイコン位置で差し替える
                   *
                   * アイコンと同じ 20px 枠にスピナーを置くことで、ラベルも幅も動かない。
                   */}
                  <button
                    type="button"
                    data-testid="mapping-export-button"
                    disabled={mappingExportDisabled}
                    aria-busy={mappingExportLoading}
                    onClick={handleDownloadMappingSetting}
                    className={
                      'inline-flex h-[45px] items-center gap-2 rounded-lg border border-[#383947] bg-white px-4 py-2 text-sm text-[#383947b3] ' +
                      (mappingExportDisabled
                        ? 'cursor-not-allowed bg-gray-300/40'
                        : 'hover:border-1.5 hover:border-[#383947] hover:font-medium hover:text-[#383947]')
                    }
                  >
                    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                      {mappingExportLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CloudDownloadIcon fontSize="small" />
                      )}
                    </span>
                    マッピング設定をダウンロード
                  </button>
                  {/* 項目マスタ（IF02090）が取得できず出力できない場合の理由表示（実装計画 5.3） */}
                  {(allChrofyColumns ?? []).length === 0 && (
                    <p className="text-caption-2 grey--text m-0">※ {MSG_MAPPING_EXPORT_DISABLED}</p>
                  )}
                </div>
              </div>

              {displayedAccountingUploadTasks.length > 0 && (
                <div
                  className="box-xy-bordered border-blue2-lighten-20 rounded-xl p-6 mb-8 mt-5"
                  data-testid="financial-data-section"
                >
                  <h3 className="text-h5 font-bold mb-3">■財務データ</h3>
                  <p className="m-0 mb-4 text-caption-1 grey--text">
                    財務データファイルのアップロード、およびCHROFY利用科目へのデータマッピングを行ってください。
                    <br />
                    財務データの登録、および詳細の確認は財務データ登録画面で行ってください。
                  </p>
                  <div className="setting-table relative">
                    <div className="overflow-x-auto">
                      <table className="base-table data-accounting-list-table w-full">
                        <thead className="text-h7">
                          <tr>
                            <th>対象</th>
                            <th>ファイル名</th>
                            <th>登録者</th>
                            <th>登録日時</th>
                            <th>科目数</th>
                            <th>ステータス</th>
                          </tr>
                        </thead>
                        <tbody className="text-caption-2">
                          {displayedAccountingUploadTasks.map((row) => {
                            const uploadable = isFinancialRowUploadable(row.status)
                            const isErr = isFinancialRowAccountingError(row.status)
                            const accPath = `/data/analysisData/${encodeURIComponent(taskKeyForRoutes)}/accountingData/${row.mode === 'consolidated' ? '2' : '1'}`
                            const rowKey = row.uploadTaskKey
                              ? String(row.uploadTaskKey)
                              : `${row.target}-${row.fileName}`
                            return (
                              <tr key={rowKey} className="border_black">
                                <td>
                                  <div className="flex middle data-upload">
                                    <div style={{ margin: '0 auto 0 0' }}>{formatAccountingTarget(row.target)}</div>
                                    {uploadable && accPath && canUploadWrite ? (
                                      <Link
                                        component={RouterLink}
                                        to={accPath}
                                        underline="none"
                                        className="blue1--text"
                                      >
                                        <span>アップロード</span>
                                      </Link>
                                    ) : (
                                      <span />
                                    )}
                                  </div>
                                </td>
                                <td className="break-words">{row.fileName}</td>
                                <td>{row.registeredBy}</td>
                                <td className="text-nowrap">{formatTaskScreenDateTime(row.registeredAt)}</td>
                                <td className="right">{formatCommaDigit(String(row.subjectCount ?? ''))}</td>
                                <td className="text-nowrap">
                                  {!isErr ? (accountingUploadTaskStatus[row.status]?.name ?? row.status) : null}
                                  {isErr && (
                                    <button
                                      type="button"
                                      className="button_status_error"
                                      onClick={() =>
                                        openAccountingUploadErrorModal(row.fileName ?? '', row.errorInfo ?? '')
                                      }
                                    >
                                      エラー詳細
                                    </button>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl mt-10 pt-10 pb-16 px-16 border-blue1 w-full max-w-none">
              <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                データ更新＆データ確定
              </h2>

              <div className="mt-8 px-6">
                <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
                  <div className="flex min-w-0 flex-col gap-3">
                    <h4 className="text-h5 font-bold m-0">■データ更新</h4>
                    <p className="text-caption-1 grey--text m-0 leading-relaxed grow">
                      データ検証が完了した項目をシステムに取り込み、各ダッシュボードのグラフのデータを更新します。処理には時間がかかります。
                      <br />
                      データファイルの追加や変更を行った場合には、再度データ更新をしてください。
                    </p>
                    <div className="w-full">
                      <button
                        type="button"
                        className={dataUpdatePrimaryButtonClassName}
                        disabled={isDataUpdateActionDisabled}
                        onClick={handleDataUpdateClick}
                      >
                        <RefreshIcon fontSize="small" className="shrink-0 text-inherit" aria-hidden />
                        データ更新
                      </button>
                    </div>
                    {/* メッセージは常に1行分確保した注記行に表示し、表示切替でボタン位置が動かないようにする */}
                    {dataUpdateInlineError ? (
                      <p
                        className={`text-caption-2 m-0 w-full ${dataUpdateInlineIsInfo ? 'grey--text' : 'red--text'}`}
                        role="alert"
                      >
                        {dataUpdateInlineIsInfo ? `※ ${dataUpdateInlineError}` : dataUpdateInlineError}
                      </p>
                    ) : (
                      <p className="text-caption-2 grey--text m-0 w-full">{dataUpdateSubNote ?? '\u00A0'}</p>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col gap-3">
                    <h4 className="text-h5 font-bold m-0">■データ確定</h4>
                    <p className="text-caption-1 grey--text m-0 leading-relaxed grow">
                      必要なデータファイルが全てアップロードされた上で、必要な項目が未設定状態になっていないことを確認の上、データ確定を行ってください。
                      <br />
                      データを確定すると対象年月のデータの変更や更新が出来なくなります。
                    </p>
                    <div className="w-full">
                      {dataCreateTask?.dataFixStatus === dataFixStatus.pending.value && (
                        <button
                          type="button"
                          className="btn inline-flex w-full min-h-[42px] items-center justify-center gap-1.5 rounded-lg !border-[#297ad8] !bg-[#297ad8] !py-2.5 px-4 text-white hover:!border-[#1c65ba] hover:!bg-[#1c65ba] disabled:!cursor-not-allowed disabled:!border-slate-200 disabled:!bg-slate-100 disabled:!text-slate-500"
                          disabled={dataFixButtonDisabled}
                          onClick={handleDataFixClick}
                        >
                          <LockOutlinedIcon fontSize="small" className="shrink-0 text-inherit" aria-hidden />
                          データ確定
                        </button>
                      )}
                      {dataCreateTask?.dataFixStatus === dataFixStatus.fix.value && (
                        <button
                          type="button"
                          className="btn btn-outline inline-flex w-full min-h-[42px] items-center justify-center gap-1.5 rounded-lg !py-2.5 px-4 disabled:!cursor-not-allowed disabled:!opacity-60"
                          disabled={dataFixCancelButtonDisabled}
                          onClick={openFixCancelModal}
                        >
                          データ確定解除
                        </button>
                      )}
                    </div>
                    {/* データ更新列と同様、メッセージ行は常に1行分を確保してボタン位置を固定する */}
                    {inlineErrors[INLINE_ERROR_KEY_DATA_FIX] ? (
                      <p className="text-caption-2 red--text m-0 w-full" role="alert">
                        {inlineErrors[INLINE_ERROR_KEY_DATA_FIX]}
                      </p>
                    ) : (
                      <p className="text-caption-2 grey--text m-0 w-full">
                        {dataCreateTask?.dataFixStatus === dataFixStatus.fix.value
                          ? '※ データファイルやマッピング設定の追加・変更を行う場合には、データ確定解除ボタンを押して解除してください。'
                          : '\u00A0'}
                      </p>
                    )}
                  </div>
                </div>

                <h3 className="text-h6 font-bold mb-3 mt-8 pt-8 border-t border-blue2-lighten-20">操作履歴</h3>
                {(() => {
                  const history = [...(operationHistory ?? [])].sort((a, b) => {
                    const dateA = a.operationStartDateTime || a.operationDateTime || ''
                    const dateB = b.operationStartDateTime || b.operationDateTime || ''
                    return dateB.localeCompare(dateA)
                  })
                  const totalCount = history.length
                  if (totalCount === 0) {
                    return (
                      <div className="text-center my-6 py-6">
                        <span className="text-body grey--text">操作履歴がありません</span>
                      </div>
                    )
                  }

                  const totalPages = Math.ceil(totalCount / historyPerPage)
                  const currentPage = Math.min(historyPage, totalPages)
                  const startIndex = (currentPage - 1) * historyPerPage
                  const paginatedHistory = history.slice(startIndex, startIndex + historyPerPage)

                  return (
                    <>
                      <table className="base-table w-full mb-4">
                        <thead className="base-table-header text-h7">
                          <tr>
                            <th>操作</th>
                            <th>開始日時</th>
                            <th>終了日時</th>
                            <th>処理時間</th>
                            <th>操作者</th>
                            <th>許諾者</th>
                            <th>許諾日</th>
                          </tr>
                        </thead>
                        <tbody className="text-caption-2">
                          {paginatedHistory.map((h, i) => {
                            const kindName =
                              operationKind[h.operationKind]?.name ||
                              (Object.values(operationKind).find((v) => v.value === h.operationKind)?.name ??
                                h.operationKind)
                            return (
                              <tr key={`${h.operationKind}-${h.operationStartDateTime}-${i}`} className="border_black">
                                <td>{kindName}</td>
                                <td className="text-nowrap">{h.operationStartDateTime || h.operationDateTime}</td>
                                <td className="text-nowrap">{h.operationEndDateTime}</td>
                                <td>{formatOperationDuration(h.operationStartDateTime, h.operationEndDateTime)}</td>
                                <td>{h.operationUser}</td>
                                <td>{h.permitUser ?? ''}</td>
                                <td className="text-nowrap">{h.permitDateTime ?? ''}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-4 mt-4">
                          <button
                            type="button"
                            className="btn btn-outline min-h-0 p-1 disabled:opacity-30"
                            disabled={currentPage === 1}
                            onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                            aria-label="前のページ"
                          >
                            <NavigateBeforeIcon />
                          </button>
                          <span className="text-caption-1 grey--text">
                            {currentPage} / {totalPages}
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline min-h-0 p-1 disabled:opacity-30"
                            disabled={currentPage === totalPages}
                            onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
                            aria-label="次のページ"
                          >
                            <NavigateNextIcon />
                          </button>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CustomTooltip title="情報更新" placement="left" arrow={true} forceShow={true}>
        <button
          type="button"
          onClick={handleRefreshFabClick}
          disabled={isSystemBusy || refreshFabLoading}
          aria-label="情報更新"
          aria-busy={refreshFabLoading}
          className="fixed bottom-8 right-8 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-[#3150a5] text-white shadow-lg hover:bg-[#3150a5cc] disabled:bg-[#3150a566] disabled:cursor-not-allowed transition-colors"
        >
          <RefreshIcon className={isSystemBusy || refreshFabLoading ? 'animate-spin' : ''} />
        </button>
      </CustomTooltip>
    </main>
  )
}

/**
 * @param {{ params?: { dataCreateTaskKey?: string } }} props
 * @returns {React.ReactElement}
 */
const AnalysisDataMain = (props) => {
  const params = props.params ?? {}
  const location = useLocation()
  /** ReportDataUpdateProviderStore より外側のコンテキストで取得（ローカル Provider に隠れない） */
  const globalReduxStore = useStore()

  const session = useSelector(
    (s) => ({
      authority: s.authority,
      userSub: s.user.sub,
      userName: s.user.userName,
      corporationNumber: s.company.corporationNumber,
      isParent: s.company.isParent,
      accPeriod: s.company.accPeriod
    }),
    shallowEqual
  )

  const uploadAuth = session.authority?.Upload_AuthInfo
  const authConf = uploadAuth?.Auth_Conf
  const canView = Boolean(uploadAuth?.Availability && authConf?.View)

  const searchParams = new URLSearchParams(location.search)
  const periodTypeFromUrl = searchParams.get('period_type') ?? searchParams.get('periodType') ?? null

  const uploadableDataKindFiles = authConf?.Uploadable_DataKind?.Files ?? []

  if (!canView) {
    return <Forbidden />
  }

  const contentProps = {
    dataCreateTaskKey: params.dataCreateTaskKey,
    userSub: session.userSub,
    userName: session.userName,
    pageTitle: titleName.analysisData,
    corporationNumber: session.corporationNumber,
    authConf,
    uploadableDataKindFiles,
    message: location.state?.message,
    globalReduxStore,
    isParent: session.isParent
  }

  return (
    // ローカルストア Provider は状態の注入のみ。権限・ファイル種別は contentProps 経由で Content が参照する
    <ReportDataUpdateProviderStore>
      <ReportDataRouteInit dataCreateTaskKeyParam={params.dataCreateTaskKey} periodTypeFromUrl={periodTypeFromUrl}>
        <AnalysisDataContent {...contentProps} />
      </ReportDataRouteInit>
    </ReportDataUpdateProviderStore>
  )
}

/**
 * 既存 Router と同じ `useParams` 注入パターンに合わせるラッパー。
 *
 * @param {Record<string, unknown>} props
 * @returns {React.ReactElement}
 */
function AnalysisData(props) {
  const params = useParams()
  return <AnalysisDataMain {...props} params={params} />
}

export default AnalysisData
